import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { lifeStateApi, stateApi, wellnessStateApi } from "@/lib/api";
import { computeStewardshipScore } from "@/lib/finance";
import { computeWellnessScore } from "@/lib/wellness";
import { toast } from "sonner";

const LifeStateContext = createContext(null);

export const LifeStateProvider = ({ children }) => {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wealthStewardship, setWealthStewardship] = useState(null);
  const [wellnessVitality, setWellnessVitality] = useState(null);
  const [saveStatus, setSaveStatus] = useState("idle"); // idle | saving | saved | error
  const saveTimer = useRef(null);
  const pendingSummary = useRef("Life dashboard updated");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await lifeStateApi.get();
      setState(data.state);
    } catch (e) {
      toast.error("Could not load your life data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Pull real Wealth data to score the Wealth & Stewardship life area.
  useEffect(() => {
    stateApi.get()
      .then((d) => setWealthStewardship(computeStewardshipScore(d.state)))
      .catch(() => setWealthStewardship(null));
  }, []);

  // Pull real Wellness data to score the Health & Vitality life area.
  useEffect(() => {
    wellnessStateApi.get()
      .then((d) => setWellnessVitality(computeWellnessScore(d.state)))
      .catch(() => setWellnessVitality(null));
  }, []);

  const doSave = useCallback(async (next, summary) => {
    setSaveStatus("saving");
    try {
      await lifeStateApi.save(next, summary);
      setSaveStatus("saved");
      setTimeout(() => setSaveStatus("idle"), 1500);
    } catch (e) {
      setSaveStatus("error");
      toast.error("Failed to save changes");
    }
  }, []);

  const update = useCallback((updater, summary = "Life dashboard updated") => {
    setState((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      pendingSummary.current = summary;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => doSave(next, pendingSummary.current), 650);
      return next;
    });
  }, [doSave]);

  const saveNow = useCallback((summary) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    if (state) doSave(state, summary || pendingSummary.current);
  }, [state, doSave]);

  return (
    <LifeStateContext.Provider value={{ state, setState, update, saveNow, loading, saveStatus, wealthStewardship, wellnessVitality, reload: load }}>
      {children}
    </LifeStateContext.Provider>
  );
};

export const useLifeState = () => useContext(LifeStateContext);
